import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { Link, Route, Router as WouterRouter, Switch, useLocation, useRoute } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Seo } from "@/components/Seo";
import { AppShell } from "@/components/AppShell";
import { CalculatorCard } from "@/components/CalculatorCard";
import { CategoryCard } from "@/components/CategoryCard";
import { SearchBox } from "@/components/SearchBox";
import { getArticleContent } from "@/content/articleContent";
import { getCategoryContent } from "@/content/categoryContent";
import {
  publicCalculators,
  categories,
  getCalculator,
  getCalculatorsByCategory,
  getCategory,
  relatedCalculators,
  WORKING_CALCULATOR_COUNT_LABEL,
  type CalculatorItem,
} from "@/data/calculators";
import { trackView } from "@/lib/recentlyViewed";
import { claimRetiredStatisticsReload } from "@/lib/retiredStatistics";
import { withTrailingSlash } from "@/lib/urls";
import {
  getDescriptiveStatisticsFaqItems,
  getStatisticsUnavailableFaqItems,
  getStatisticsUnavailableCalculatorProfile,
  getStatisticsCalculatorDescription,
} from "@/data/statisticsCalculatorSupport";
import { Home } from "@/pages/Home";

const SuperCategoryPage = lazy(() =>
  import("@/pages/calculators/SuperCategoryPage").then((m) => ({ default: m.SuperCategoryPage }))
);
const BlogHome = lazy(() =>
  import("@/pages/BlogHome").then((m) => ({ default: m.BlogHome }))
);
const BlogPostPage = lazy(() =>
  import("@/pages/BlogPost").then((m) => ({ default: m.BlogPostPage }))
);
const BlogCategoryPageWrapper = lazy(() =>
  import("@/pages/BlogCategory").then((m) => ({ default: m.BlogCategoryPageWrapper }))
);
const LegalHub = lazy(() =>
  import("@/components/LegalHub").then((m) => ({ default: m.LegalHub }))
);

const CalculatorEngine = lazy(() =>
  import("@/components/CalculatorEngine").then((m) => ({ default: m.CalculatorEngine }))
);

const MortgageCalculator = lazy(() =>
  import("@/components/MortgageCalculator").then((m) => ({ default: m.MortgageCalculator }))
);

// ── Disable browser scroll-restoration so SPAs always start at the top ──────
if (typeof window !== "undefined") {
  window.history.scrollRestoration = "manual";
}

function ScrollToTop() {
  const [path] = useLocation();
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "instant" });
      }
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [path]);
  return null;
}

const queryClient = new QueryClient();

// ── FAQ helpers (used by CalculatorPage) ─────────────────────────────────────

const categoryContext: Record<string, { usedFor: string; exactNote: string; professionalNote: string; lawsNote: string; accuracyTip: string }> = {
  finance: {
    usedFor: "budget, loan, savings, investment, and tax planning decisions",
    exactNote: "Results are estimates based on standard US financial formulas. Actual loan rates, tax brackets, and investment returns vary by lender, IRS rules, and market conditions.",
    professionalNote: "For tax filing, investment advice, or legal financial matters, consult a licensed CPA, financial advisor, or attorney.",
    lawsNote: "US tax code, IRS Publication guidance, state income tax rules, CFPB lending regulations, and SEC investment rules change annually — verify with IRS.gov or a qualified advisor.",
    accuracyTip: "Enter the exact interest rate (APR), loan term in months, and principal balance. Small rounding errors compound over time, so use precise figures from your statement or offer letter.",
  },
  health: {
    usedFor: "estimating health metrics, fitness goals, nutrition targets, and wellness benchmarks",
    exactNote: "Health calculators use population-level formulas (e.g. Mifflin-St Jeor, BMI) that are statistical averages. Individual results vary based on genetics, medical history, and body composition.",
    professionalNote: "Never use these results to diagnose, treat, or replace advice from a licensed physician, registered dietitian, or certified health professional.",
    lawsNote: "Clinical guidelines from the CDC, NIH, AHA, and your state health department set the standards for medical thresholds like healthy BMI, blood pressure ranges, and calorie recommendations.",
    accuracyTip: "Use measured values rather than estimates for height, weight, and age. Take measurements consistently — same time of day and same conditions — for reliable tracking.",
  },
  math: {
    usedFor: "solving arithmetic, algebra, geometry, and general mathematics problems step by step",
    exactNote: "These calculators apply exact mathematical formulas. Rounding only occurs in the displayed result — intermediate steps use full precision.",
    professionalNote: "For academic assessments, exams, or published research, verify results using your course textbook or a peer-reviewed source.",
    lawsNote: "Mathematical formulas are universal, but applied math problems in engineering, finance, or science may require discipline-specific standards or notation conventions.",
    accuracyTip: "Double-check that you are using the correct formula variant for your problem. For example, area formulas differ between 2D shapes and the surface area of 3D solids.",
  },
  construction: {
    usedFor: "estimating materials, costs, dimensions, and quantities for building and renovation projects",
    exactNote: "Estimates include standard waste allowances but actual quantities depend on site conditions, installer skill, material cuts, and supplier specifications.",
    professionalNote: "Structural, electrical, plumbing, and load-bearing calculations must be reviewed and approved by a licensed engineer or contractor before construction.",
    lawsNote: "Building codes (IBC, IRC), local planning rules, zoning ordinances, and HOA restrictions vary by state and county. Check with your local building authority before starting any project.",
    accuracyTip: "Measure twice, enter exact dimensions, and add the recommended waste percentage (typically 10–15%) when ordering materials. Prices vary by region and supplier.",
  },
  business: {
    usedFor: "evaluating business metrics, financial ratios, marketing performance, and operational efficiency",
    exactNote: "Results use standard accounting and business formulas. Actual performance depends on your industry, market conditions, company size, and accounting methods.",
    professionalNote: "Business valuations, financial statements, tax filings, and investor reports require sign-off from a licensed CPA or certified financial analyst.",
    lawsNote: "SEC reporting rules, GAAP/IFRS accounting standards, state business tax laws, and industry-specific regulations govern how metrics should be calculated and disclosed.",
    accuracyTip: "Use audited financial figures where available. Confirm whether your revenue and cost figures are on an accrual or cash basis — this significantly changes profitability metrics.",
  },
  physics: {
    usedFor: "calculating physical quantities including force, energy, motion, waves, and electromagnetism",
    exactNote: "Calculations use classical physics formulas. Relativistic or quantum effects apply at extreme scales — results are accurate for everyday engineering and educational purposes.",
    professionalNote: "Safety-critical physics calculations for aerospace, nuclear, or structural applications must be validated by a licensed Professional Engineer.",
    lawsNote: "Physics constants and SI unit definitions are set by NIST and the BIPM. Engineering applications must also comply with relevant ISO and ANSI standards.",
    accuracyTip: "Always check that units are consistent (SI vs imperial) before entering values. Converting all inputs to the same unit system avoids large systematic errors.",
  },
  engineering: {
    usedFor: "solving engineering design problems including stress, load, flow, electrical, and thermal calculations",
    exactNote: "Results apply standard engineering formulas with typical safety assumptions. Real-world designs require additional factors for material tolerances, fatigue, and environmental conditions.",
    professionalNote: "Any calculation used in a physical design must be checked and stamped by a licensed Professional Engineer (PE) before construction or manufacture.",
    lawsNote: "ASME, ASTM, IEEE, IEC, and ISO standards define allowable tolerances, material grades, and design methods. Local building codes and OSHA regulations also apply.",
    accuracyTip: "Use nominal material properties as a starting point, then apply appropriate safety factors. Verify material grades and load conditions match your actual application.",
  },
  chemistry: {
    usedFor: "calculating concentrations, reaction quantities, thermodynamic properties, and chemical equilibria",
    exactNote: "Results use ideal or standard-state assumptions. Real solutions may deviate due to activity coefficients, non-ideal behaviour, and impurities.",
    professionalNote: "Laboratory procedures, pharmaceutical formulations, and industrial chemical processes must be designed and approved by a licensed chemist or chemical engineer.",
    lawsNote: "OSHA hazard communication standards, EPA chemical reporting rules, and FDA purity requirements govern chemical handling and disclosure in the US.",
    accuracyTip: "Check your significant figures, use molar masses from a reliable periodic table, and confirm that temperature and pressure match standard state conditions if required by the formula.",
  },
  statistics: {
    usedFor: "running descriptive statistics, probability calculations, hypothesis tests, and regression analyses",
    exactNote: "Results assume the statistical model and distribution you select are appropriate for your data. Always check that assumptions (normality, independence, equal variance) are satisfied.",
    professionalNote: "Published research, clinical trials, and regulatory submissions require peer review and statistical sign-off from a qualified biostatistician.",
    lawsNote: "FDA guidance on clinical trial statistics, APA reporting standards, and institutional IRB requirements govern how statistical results must be reported and interpreted.",
    accuracyTip: "Confirm your sample is representative and that you are using the correct test for your data type (parametric vs non-parametric). Check for outliers before running any analysis.",
  },
  sports: {
    usedFor: "calculating training metrics, race paces, power outputs, calorie burns, and performance benchmarks",
    exactNote: "Sports formulas use population averages derived from research studies. Individual results vary based on fitness level, body composition, and training history.",
    professionalNote: "For injury management, competitive training plans, or performance diagnostics, consult a certified sports coach, physiotherapist, or sports medicine physician.",
    lawsNote: "World Athletics, USA Track & Field, USCF, USA Swimming, and other governing bodies set official competition rules, record standards, and anti-doping regulations.",
    accuracyTip: "Use measured test results (e.g. VO2 max from a lab test, actual race time) rather than estimates for the most accurate training zone and pace calculations.",
  },
  food: {
    usedFor: "calculating nutrition, recipe scaling, ingredient ratios, and cooking measurements",
    exactNote: "Nutritional values are based on USDA database averages and may differ from actual food labels, cooking methods, and ingredient brands.",
    professionalNote: "Therapeutic diets, clinical nutrition plans, and food labelling for commercial products must be developed or reviewed by a registered dietitian or food scientist.",
    lawsNote: "FDA nutrition labelling regulations, USDA dietary guidelines, and allergen disclosure laws govern commercial food products. State health codes apply to food service businesses.",
    accuracyTip: "Use weight measurements (grams) rather than volume (cups) for greater precision in baking and nutrition calculations. Brand-specific values may differ from generic database entries.",
  },
  ecology: {
    usedFor: "measuring carbon footprints, energy savings, water usage, and environmental impact",
    exactNote: "Emission factors and energy conversion values are based on EPA and EIA averages. Your actual impact depends on your local grid mix, supplier, and usage patterns.",
    professionalNote: "Corporate carbon disclosures, regulatory environmental impact assessments, and verified offset projects require sign-off from accredited environmental consultants.",
    lawsNote: "EPA reporting requirements, the Clean Air Act, state renewable portfolio standards, and SEC climate disclosure rules govern environmental claims and sustainability reporting.",
    accuracyTip: "Use your actual utility bills rather than estimates for energy and water inputs. Regional emission factors vary significantly — use your local grid's published carbon intensity where possible.",
  },
  education: {
    usedFor: "calculating grades, GPAs, study hours, test scores, and academic performance metrics",
    exactNote: "Grade calculations use the weighting and scale you enter. Different institutions use different GPA scales (4.0, 5.0, percentage), so check your school's official grading policy.",
    professionalNote: "Official transcripts, admissions decisions, and scholarship eligibility are determined solely by your institution. Use these results for personal planning only.",
    lawsNote: "FERPA protects student records in the US. Admissions requirements, GPA thresholds, and academic standing rules are set by each institution and may differ from federal guidance.",
    accuracyTip: "Enter exact credit hours and the grade points your institution assigns to each letter grade. An A-minus may be 3.7 at one school and 4.0 at another.",
  },
  technology: {
    usedFor: "calculating bandwidth, storage, API costs, cloud infrastructure, and digital performance metrics",
    exactNote: "Results use standard SI and binary unit definitions. Cloud pricing, bandwidth costs, and hardware specs vary by provider, region, and contract tier.",
    professionalNote: "Production infrastructure sizing, security architecture, and compliance configurations should be reviewed by a qualified software architect or cloud engineer.",
    lawsNote: "GDPR, CCPA, HIPAA, and PCI-DSS set data handling and storage requirements. Cloud providers publish their own SLAs and shared responsibility models.",
    accuracyTip: "Check whether your provider uses SI units (1 GB = 1,000 MB) or binary units (1 GiB = 1,024 MiB) — the difference matters significantly for storage billing calculations.",
  },
  conversion: {
    usedFor: "converting between units of length, weight, temperature, volume, speed, and other physical quantities",
    exactNote: "Conversion factors are exact or use the highest published precision. Rounding in the result display does not affect the underlying conversion accuracy.",
    professionalNote: "Legal, trade, and medical unit conversions may require certified calibration or specific standards — confirm with the relevant authority.",
    lawsNote: "The US uses USC units while most of the world uses SI. NIST Handbook 44 governs weights and measures in US commerce, and trade labelling laws specify which units must be displayed.",
    accuracyTip: "For chained conversions (e.g. feet → metres → inches), use a single conversion factor rather than two sequential steps to avoid compounding rounding errors.",
  },
  "everyday-life": {
    usedFor: "quick everyday calculations for tips, bills, budgets, travel time, and common household decisions",
    exactNote: "Results are practical estimates for everyday use. Actual costs and values depend on local prices, rates, and individual circumstances.",
    professionalNote: "For significant financial, legal, or health decisions that arise from everyday calculations, verify with the relevant professional or official source.",
    lawsNote: "Tip customs, sales tax rates, local transport rules, and bill regulations vary by US state and city. Check local rules for any legally significant amounts.",
    accuracyTip: "Enter the most current prices and rates you have — fuel prices, utility rates, and local taxes change frequently and have a significant effect on the result.",
  },
};

const getFaqItems = (calculator: CalculatorItem) => {
  if (calculator.categorySlug === "statistics") {
    const unavailableFaqs = getStatisticsUnavailableFaqItems(calculator.title);
    if (unavailableFaqs) return unavailableFaqs;
    const descriptiveFaqs = getDescriptiveStatisticsFaqItems(calculator.title);
    if (descriptiveFaqs) return descriptiveFaqs;
  }
  if (calculator.type === "qr_code_generator_calc") {
    return [
      { question: "What is a QR code?", answer: "A QR code is a two-dimensional barcode that stores text, links, contact details, Wi-Fi settings, or other data that a compatible camera or scanning app can read." },
      { question: "How do I create a QR code?", answer: "Choose the content type, enter the requested information, review the preview, then use Generate QR code. You can download, copy, print, or share the finished code." },
      { question: "Is this QR Code Generator free?", answer: "Yes. It works in the browser with no account required and does not upload the content you enter." },
      { question: "Can I create a QR code for a website?", answer: "Yes. Choose Website, enter an http:// or https:// address, and optionally add UTM campaign fields before generating the code." },
      { question: "Can I create a QR code for Wi-Fi?", answer: "Yes. Choose Wi-Fi and enter the network name, security type, and password. Anyone who can scan the result may be able to access that network." },
      { question: "Can I create a QR code for a contact card?", answer: "Yes. The Contact format creates a vCard payload with the name, phone, email, company, website, and optional address details you provide." },
      { question: "Can I put a logo inside a QR code?", answer: "This version keeps the code clear and scannable by using colour, size, margin, and error-correction controls rather than automatically obscuring modules with a logo." },
      { question: "Can I customise QR code colours?", answer: "Yes, but keep strong contrast between the code and its background. Decorative colour choices should never make the modules difficult for scanners to distinguish." },
      { question: "What is QR code error correction?", answer: "Error correction adds redundant data so a code can still work if it is partly dirty or obscured. Higher levels improve resilience but can make the QR denser." },
      { question: "How much information can a QR code store?", answer: "Capacity depends on the data mode, character set, QR version, and error-correction level. Shorter payloads generally create simpler, more reliable codes." },
      { question: "Do QR codes expire?", answer: "A static QR code does not have an automatic expiry date. It can stop being useful if its website, app link, contact information, or other destination changes or disappears." },
      { question: "Can QR codes be edited after they are created?", answer: "A static code cannot be edited in place. Generate a new code when the encoded content changes." },
      { question: "Are QR codes safe?", answer: "A QR code is a data container, not a safety guarantee. Check the destination before opening it, and do not encode or share sensitive information unless you understand who can scan it." },
      { question: "Can I download my QR code as SVG or PNG?", answer: "Yes. After generation, use the PNG or SVG download action. SVG is useful for scalable print artwork; PNG is convenient for documents and screens." },
      { question: "Can I generate multiple QR codes at once?", answer: "The current generator creates one code at a time. Batch CSV generation is a separate advanced workflow and should be added only with local processing and clear validation." },
    ];
  }
  const ctx = categoryContext[calculator.categorySlug] ?? {
    usedFor: `${calculator.categoryName.toLowerCase()} calculations`,
    exactNote: "Results depend on the quality of your inputs, formula assumptions, and topic-specific factors not included in a simplified online model.",
    professionalNote: "Important decisions should be checked against current laws, official rules, contracts, standards, or a qualified professional in your location.",
    lawsNote: `Rules vary by country, state, industry, and professional body. For ${calculator.categoryName.toLowerCase()} topics, confirm the latest regulations before acting.`,
    accuracyTip: "Use the most precise inputs available. Check that units match the labels and read the formula note to understand what the calculation assumes.",
  };
  const topic = calculator.title.toLowerCase().replace(/ calculator$/i, "");
  return [
    {
      question: `What is the ${calculator.title} used for?`,
      answer: `The ${calculator.title} is a free online tool for ${ctx.usedFor}. It lets you estimate ${topic} quickly without memorising the formula — useful for planning, learning, and comparing scenarios before making a decision.`,
    },
    {
      question: `How accurate is this ${calculator.title.toLowerCase()}?`,
      answer: ctx.exactNote,
    },
    {
      question: `Can I use this ${calculator.title.toLowerCase()} for professional or legal decisions?`,
      answer: ctx.professionalNote,
    },
    {
      question: `What US rules or standards apply to ${topic} calculations?`,
      answer: ctx.lawsNote,
    },
    {
      question: `How do I get the most accurate result from the ${calculator.title.toLowerCase()}?`,
      answer: ctx.accuracyTip,
    },
    {
      question: "Why does this page include a detailed explanation alongside the calculator?",
      answer: `Detailed pages help you understand not just the ${topic} number but what it means, what the formula assumes, when to be cautious, and what steps to take next. That context makes the result more useful and trustworthy as a reference.`,
    },
  ];
};

function faqSchema(calculator: CalculatorItem) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: getFaqItems(calculator).map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

// ── Share buttons (used by CalculatorPage) ───────────────────────────────────

function ShareButtons({ title }: { title: string }) {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const text = `I used ${title} on United Calculator — try it yourself!`;
  const enc = encodeURIComponent;

  const buttons = [
    { label: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`, color: "#000" },
    { label: "Facebook", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`, color: "#1877f2" },
    { label: "LinkedIn", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`, color: "#0a66c2" },
    { label: "WhatsApp", href: `https://wa.me/?text=${enc(text + " " + url)}`, color: "#25d366" },
    { label: "Reddit", href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(title)}`, color: "#ff4500" },
    { label: "Email", href: `mailto:?subject=${enc(title)}&body=${enc(text + "\n" + url)}`, color: "#475569" },
    { label: "Telegram", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`, color: "#26a5e4" },
  ];

  const [copiedLink, setCopiedLink] = useState(false);
  const copyLink = () => {
    navigator.clipboard.writeText(url).catch(() => {});
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <section className="share-section">
      <p className="eyebrow">Share this calculator</p>
      <div className="share-buttons">
        {buttons.map((btn) => (
          <a
            key={btn.label}
            href={btn.href}
            target="_blank"
            rel="noopener noreferrer"
            className="share-btn"
            style={{ "--share-color": btn.color } as React.CSSProperties}
            aria-label={`Share on ${btn.label}`}
          >
            {btn.label}
          </a>
        ))}
        <button
          type="button"
          className="share-btn share-btn-copy"
          onClick={copyLink}
          aria-label="Copy link"
        >
          {copiedLink ? "Copied!" : "Copy Link"}
        </button>
      </div>
    </section>
  );
}

// ── 404 page ─────────────────────────────────────────────────────────────────

function Page404() {
  const [query, setQuery] = useState("");
  return (
    <AppShell>
      <Seo
        title="Page Not Found"
        description={`Page not found. Browse ${WORKING_CALCULATOR_COUNT_LABEL} free calculators by category at UnitedCalculator — finance, health, math, science and more.`}
        canonicalPath="/"
      />
      <main className="page-main">
        <div className="page-404">
          <p className="eyebrow">404 — page not found</p>
          <h1>We couldn't find that page</h1>
          <p>The URL may be mistyped or the page may have moved. Try searching below or browse a category.</p>
          <Link href="/" className="button primary">Back to home</Link>
          <div className="page-404-grid section-block">
            <div className="section-heading">
              <p className="eyebrow">Search the library</p>
              <h2>Find a calculator</h2>
            </div>
            <SearchBox value={query} onChange={setQuery} />
            {query.trim() && (
              <div className="card-grid" style={{ marginTop: "1.5rem" }}>
                {publicCalculators
                  .filter((c) =>
                    [c.title, c.description, c.categoryName, ...c.keywords]
                      .join(" ")
                      .toLowerCase()
                      .includes(query.trim().toLowerCase())
                  )
                  .slice(0, 9)
                  .map((c) => <CalculatorCard key={c.slug} calculator={c} />)}
              </div>
            )}
            <div className="section-heading" style={{ marginTop: "2.5rem" }}>
              <p className="eyebrow">Or browse a category</p>
              <h2>All categories</h2>
            </div>
            <div className="category-grid">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.slug}
                  category={cat}
                  count={getCalculatorsByCategory(cat.slug).length}
                />
              ))}
            </div>
          </div>
        </div>
      </main>
    </AppShell>
  );
}

// ── Category page ─────────────────────────────────────────────────────────────

function CategoryPage() {
  const [, params] = useRoute("/category/:slug");
  const slug = params?.slug ?? "";
  const category = getCategory(slug);

  if (!category) return <Page404 />;

  const categoryCalculators = getCalculatorsByCategory(category.slug);
  const topCalcs = categoryCalculators.slice(0, 5);
  const catContent = getCategoryContent(category.slug, topCalcs);

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
      { "@type": "ListItem", position: 2, name: `${category.name} Calculators`, item: window.location.href },
    ],
  };

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${category.name} Calculators`,
    description: `Browse ${categoryCalculators.length} ${category.name.toLowerCase()} calculators with formulas, examples, FAQs, and related guides.`,
    url: window.location.href,
    numberOfItems: categoryCalculators.length,
  };

  return (
    <AppShell>
      <Seo
        title={`${category.name} Calculators`}
        description={`Browse ${categoryCalculators.length} ${category.name.toLowerCase()} calculators with interactive tools, formulas, examples, FAQs, legal context, and related calculator guides.`}
        canonicalPath={`/category/${category.slug}`}
        schema={[breadcrumbSchema, collectionSchema]}
      />
      <main className="page-main">
        <nav className="breadcrumbs" aria-label="Breadcrumbs">
          <Link href="/">Home</Link><span>/</span><span>{category.name}</span>
        </nav>
        <section
          className="category-hero"
          style={{ "--cat-color": category.color } as React.CSSProperties}
        >
          <div className="cat-color-bar" />
          <div>
            <p className="eyebrow">{categoryCalculators.length} tools</p>
            <h1><em>{category.name}</em> Calculators</h1>
            <p>{category.summary}</p>
            <div className="keyword-row">
              {category.keywords.map((keyword) => (
                <Link key={keyword} href={`/?q=${encodeURIComponent(keyword)}`} className="keyword-link">{keyword}</Link>
              ))}
            </div>
          </div>
        </section>
        <section className="top-picks-section">
          <div className="section-heading">
            <p className="eyebrow">Start here</p>
            <h2>Top {category.name.toLowerCase()} calculators</h2>
            <p>The most-used tools in this category — each with a formula, worked example, FAQs, and 8 related calculators.</p>
          </div>
          <div className="top-picks-grid">
            {categoryCalculators.slice(0, 6).map((calc, i) => (
              <Link key={calc.slug} href={withTrailingSlash(`/calculator/${calc.slug}`)} className="top-pick-card">
                <span className="pick-rank" aria-hidden="true">{i + 1}</span>
                <div className="pick-body">
                  <h3>{calc.title}</h3>
                  <p>{calc.description.slice(0, 100)}{calc.description.length > 100 ? "…" : ""}</p>
                  <div className="pick-keywords">
                    {calc.keywords.slice(0, 3).map((kw) => (
                      <span key={kw} className="keyword-link pick-kw">{kw}</span>
                    ))}
                  </div>
                </div>
                <ArrowRight size={16} className="pick-arrow" />
              </Link>
            ))}
          </div>
        </section>

        <section className="content-layout">
          <div className="article-copy">
            <h2>About this {category.name.toLowerCase()} calculator collection</h2>
            {catContent.intro}
            <h3>How this collection is organised</h3>
            {catContent.howWeOrganise}
            <h3>Relevant laws, regulations, and professional guidance</h3>
            {catContent.lawsAndRules}
          </div>
          <aside className="stat-stack">
            <div><strong>{categoryCalculators.length}</strong><span>calculator pages</span></div>
            <div><strong>{category.keywords.length}</strong><span>topic keywords</span></div>
            <div>
              <strong style={{ fontSize: "1rem", lineHeight: 1.4 }}>
                {catContent.keyTopics.slice(0, 4).join(" · ")}
              </strong>
              <span>popular topics</span>
            </div>
          </aside>
        </section>
        <div className="card-grid wide">
          {categoryCalculators.map((calculator) => <CalculatorCard key={calculator.slug} calculator={calculator} />)}
        </div>
      </main>
    </AppShell>
  );
}

// ── Calculator page ───────────────────────────────────────────────────────────

function RetiredStatisticsPage({ calculator }: { calculator: CalculatorItem }) {
  const message =
    "This Statistics calculator has been permanently removed. Browse current calculators or return to the home page.";
  return (
    <AppShell>
      <Seo
        title="410 — Calculator no longer available"
        description={message}
        canonicalPath={`/calculator/${calculator.slug}`}
        robots="noindex, nofollow"
      />
      <main className="page-main">
        <section className="page-404" role="status" aria-labelledby="retired-calculator-title">
          <p className="eyebrow">410 — Gone</p>
          <h1 id="retired-calculator-title">{calculator.title} is no longer available</h1>
          <p>{message}</p>
          <nav aria-label="Continue browsing">
            <Link href="/category/statistics/" className="button primary">
              Browse Statistics calculators
            </Link>
            <Link href="/" className="button">
              UnitedCalculator home
            </Link>
          </nav>
        </section>
      </main>
    </AppShell>
  );
}

function CalculatorPage() {
  const [, params] = useRoute("/calculator/:slug");
  const slug = params?.slug ?? "";
  const calculator = getCalculator(slug);
  const statisticsAvailability =
    calculator?.categorySlug === "statistics"
      ? getStatisticsUnavailableCalculatorProfile(calculator.title)
      : null;

  useEffect(() => {
    if (calculator && !statisticsAvailability) {
      trackView({
        slug: calculator.slug,
        title: calculator.title,
        categoryName: calculator.categoryName,
        categorySlug: calculator.categorySlug,
      });
    }
  }, [calculator?.slug, Boolean(statisticsAvailability)]);

  useEffect(() => {
    try {
      if (
        statisticsAvailability &&
        claimRetiredStatisticsReload(window.location.pathname, window.sessionStorage)
      ) {
        window.location.reload();
      }
    } catch {
      // Keep the in-app 410 state visible when browser storage is unavailable.
    }
  }, [slug, statisticsAvailability?.topic]);

  if (!calculator) return <Page404 />;
  if (statisticsAvailability) {
    return <RetiredStatisticsPage calculator={calculator} />;
  }

  const category = getCategory(calculator.categorySlug);
  const related = relatedCalculators(calculator);
  const sidebarRelated = related.slice(0, 4);
  const faqItems = getFaqItems(calculator);
  const content = getArticleContent(calculator, related);
  const statisticsPageDescription =
    calculator.categorySlug === "statistics"
      ? getStatisticsCalculatorDescription(calculator.title)
      : undefined;
  const calculatorArticleDescription =
    statisticsPageDescription ?? calculator.description;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${window.location.origin}/` },
      { "@type": "ListItem", position: 2, name: calculator.categoryName, item: `${window.location.origin}${withTrailingSlash(`/category/${calculator.categorySlug}`)}` },
      { "@type": "ListItem", position: 3, name: calculator.title, item: window.location.href },
    ],
  };

  const appCategoryMap: Record<string, string> = {
    finance: "FinanceApplication",
    health: "HealthApplication",
    math: "EducationalApplication",
    physics: "EducationalApplication",
    chemistry: "EducationalApplication",
    statistics: "EducationalApplication",
    education: "EducationalApplication",
    construction: "UtilitiesApplication",
    engineering: "UtilitiesApplication",
    conversion: "UtilitiesApplication",
    business: "BusinessApplication",
    "everyday-life": "LifestyleApplication",
    sports: "SportsApplication",
    food: "LifestyleApplication",
    ecology: "LifestyleApplication",
    technology: "UtilitiesApplication",
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to use the ${calculator.title}`,
    description: `Step-by-step guide to getting an accurate result from the ${calculator.title} on United Calculator.`,
    totalTime: "PT2M",
    tool: [{ "@type": "HowToTool", name: "Web browser" }],
    step: [
      { "@type": "HowToStep", position: 1, name: "Review the pre-filled example values", text: "The calculator opens with smart default inputs based on published benchmarks. The pre-filled values produce a real example result immediately — read it to understand what the tool measures before changing any numbers." },
      { "@type": "HowToStep", position: 2, name: "Enter your own values", text: `Click into each input field and type your value. Click the ? icon next to any label for a plain-language explanation. For the ${calculator.title}, check that your units match the field labels (for example, years vs months, or metres vs feet).` },
      { "@type": "HowToStep", position: 3, name: "Read your result", text: "The result card updates in real time as you type. The calculated value and unit are displayed prominently. Use the Copy result button to copy the answer to your clipboard." },
      { "@type": "HowToStep", position: 4, name: "Verify the formula", text: "The formula used for the calculation is shown in a code block below the result. A worked example with real numbers follows so you can cross-check the arithmetic independently." },
      { "@type": "HowToStep", position: 5, name: "Read the context, FAQs, and related calculators", text: "Scroll down for professional and legal guidance relevant to this calculation, 6 plain-language frequently asked questions, and 8 related calculators covering similar topics in the same and adjacent categories." },
    ],
  };

  const techArticleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: `${calculator.title}: Formula, Examples & FAQs`,
    description: calculatorArticleDescription,
    datePublished: "2026-04-20",
    dateModified: "2026-04-20",
    author: { "@type": "Organization", name: "United Calculator", url: window.location.origin },
    publisher: {
      "@type": "Organization",
      name: "United Calculator",
      url: window.location.origin,
      logo: { "@type": "ImageObject", url: `${window.location.origin}/og-image.svg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": window.location.href },
    image: `${window.location.origin}/og-image.svg`,
    articleSection: calculator.categoryName,
    keywords: calculator.keywords.join(", "),
    inLanguage: "en",
  };

  const webAppSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: calculator.title,
    url: window.location.href,
    description: calculatorArticleDescription,
    applicationCategory: appCategoryMap[calculator.categorySlug] ?? "UtilitiesApplication",
    operatingSystem: "Web",
    browserRequirements: "Requires JavaScript",
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD", availability: "https://schema.org/InStock" },
    featureList: [
      "Interactive real-time calculator",
      "Formula explanation with worked example",
      "Smart pre-filled defaults",
      "Reset and copy-to-clipboard buttons",
      "Field tooltips with plain-language help",
      "6 frequently asked questions",
      "8 related calculators",
      "Social sharing buttons",
      "Legal and professional guidance",
    ],
  };

  return (
    <AppShell>
      <Seo
        title={calculator.title}
        description={statisticsPageDescription ??
          `${calculator.title}: interactive calculator with formula explanation, worked example, legal context, FAQs, and related ${calculator.categoryName.toLowerCase()} calculators.`}
        canonicalPath={`/calculator/${calculator.slug}`}
        type="article"
        schema={[
          faqSchema(calculator),
          breadcrumbSchema,
          webAppSchema,
          howToSchema,
          techArticleSchema,
        ]}
      />
      <main className="page-main">
        <div className="calc-page-layout">
          <nav className="breadcrumbs calc-page-breadcrumb" aria-label="Breadcrumbs">
            <Link href="/">Home</Link><span>/</span>
            <Link href={withTrailingSlash(`/category/${calculator.categorySlug}`)}>{calculator.categoryName}</Link>
            <span>/</span>
            <span>{calculator.title}</span>
          </nav>
          <article className="calculator-article">
            <header className="calculator-title-block" style={{ "--cat-color": category?.color ?? "#1d6fa4" } as React.CSSProperties}>
              <p className="eyebrow">{calculator.categoryName} calculator</p>
              <h1>{calculator.title}</h1>
              <p>
                {statisticsPageDescription ??
                  calculator.description}
              </p>
              <p className="article-byline">
                <time dateTime={new Date().toISOString().slice(0, 10)}>Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</time>
                {" · "}Free tool — no signup required
              </p>
              <div className="keyword-row">
                {calculator.keywords.slice(0, 7).map((keyword) => (
                  <Link key={keyword} href={`/?q=${encodeURIComponent(keyword)}`} className="keyword-link">{keyword}</Link>
                ))}
              </div>
            </header>

            <div id="calculator">
              <Suspense fallback={<div className="engine-loading">Loading calculator…</div>}>
                <CalculatorEngine calculator={calculator} />
              </Suspense>
            </div>

            <section className="article-copy">
              <h2>What is the {calculator.title}?</h2>
              {content.intro}
              <h2>How to use this {calculator.title.toLowerCase()}</h2>
              {content.howTo}
              <h2>{content.formula.h2}</h2>
              {content.formula.body}
              <h2>{content.results.h2}</h2>
              <h3>{content.results.h3}</h3>
              {content.results.body}
              <h2>{calculator.categoryName} tips and best practices</h2>
              {content.tips}
              <h2>{calculator.title.replace(" Calculator", "")} by the numbers</h2>
              {content.byTheNumbers}
              <h2>Common mistakes to avoid</h2>
              {content.mistakes}
              <h2>Laws, rules, and professional guidance</h2>
              {content.legalNote}
              <h3>When to verify with an authority</h3>
              <p>
                Verify results with an official source whenever money, health, safety, legal rights, school records, employment, insurance, taxes, permits, or compliance obligations are involved. This calculator is for general information and education only — not legal, financial, medical, engineering, tax, or professional advice.
              </p>
              <h2>Frequently asked questions</h2>
              <div className="faq-list">
                {faqItems.map((item, index) => (
                  <details key={item.question} open={index === 0}>
                    <summary>{item.question}</summary>
                    <p>{item.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <ShareButtons title={calculator.title} />

            <section className="section-block compact">
              <div className="section-heading">
                <p className="eyebrow">You might also need:</p>
                <h2>Related {category?.name ?? calculator.categoryName} calculators</h2>
                <p>Eight related tools — 4 from the same category, 2 by formula type, 2 cross-category matches.</p>
              </div>
              <div className="related-grid">
                {related.map((item) => <CalculatorCard key={item.slug} calculator={item} />)}
              </div>
            </section>

          </article>

          <aside className="calc-sidebar" aria-label="Related calculators">
            <div className="sidebar-sticky">
              <p className="sidebar-heading">Related calculators</p>
              <div className="sidebar-cards">
                {sidebarRelated.map((item) => (
                  <Link key={item.slug} href={withTrailingSlash(`/calculator/${item.slug}`)} className="sidebar-card">
                    <span className="sidebar-card-cat">{item.categoryName}</span>
                    <span className="sidebar-card-title">{item.title}</span>
                    <ArrowRight size={13} className="sidebar-card-arrow" />
                  </Link>
                ))}
              </div>
              <Link href={withTrailingSlash(`/category/${calculator.categorySlug}`)} className="sidebar-see-all">
                See all {category?.name} calculators <ArrowRight size={12} />
              </Link>
            </div>
          </aside>
        </div>
      </main>
    </AppShell>
  );
}

// ── Legal pages ───────────────────────────────────────────────────────────────

const legalContent: Record<string, { title: string; description: string; body: ReactNode }> = {
  "/about": {
    title: "About United Calculator",
    description: "Learn about United Calculator, a searchable library of practical calculators with formulas, examples, FAQs, and related guides.",
    body: (
      <>
        <h2>Our calculator library</h2>
        <p>United Calculator provides a growing collection of free online calculators for finance, health, math, construction, conversion, science, business, education, technology, and everyday planning. Each page is built to combine an interactive tool with formula explanations, examples, FAQs, and related calculators.</p>
        <h2>How pages are organized</h2>
        <p>The site uses category pages, related calculator links, and searchable calculator cards so visitors can move from a broad topic to a specific calculator quickly. The goal is to make answers easier to calculate and easier to understand.</p>
        <h3>Editorial approach</h3>
        <p>United Calculator content is general educational information. We focus on plain-language explanations, clear assumptions, and reminders to verify professional, legal, medical, financial, tax, safety, and compliance matters with qualified sources.</p>
      </>
    ),
  },
  "/disclaimer": {
    title: "Legal Disclaimer",
    description: "Read the United Calculator legal disclaimer for estimates, professional advice, accuracy, third-party ads, and advertising placements.",
    body: (
      <>
        <h2>General information only</h2>
        <p>United Calculator is provided for general informational and educational purposes only. Calculators, examples, formulas, articles, FAQs, and related content may not reflect the latest rules, prices, laws, taxes, codes, health guidance, school policies, or professional standards.</p>
        <h2>No professional advice</h2>
        <p>The site does not provide legal, financial, tax, medical, engineering, construction, insurance, investment, or other professional advice. Always consult an appropriate professional or official source before relying on calculator results for important decisions.</p>
        <h2>Advertising</h2>
        <p>Clearly labeled ad slot placeholders are included throughout the site so the layout is ready for Google AdSense review and future display ads. Placeholder boxes are not paid advertisements until actual ad code is approved and installed.</p>
        <h3>Accuracy and availability</h3>
        <p>We aim to present useful formulas and explanations, but we do not guarantee completeness, accuracy, uninterrupted access, or fitness for a particular purpose.</p>
      </>
    ),
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description: "Privacy Policy for United Calculator covering general use, analytics, cookies, advertising, and third-party services.",
    body: (
      <>
        <h2>Information we may collect</h2>
        <p>United Calculator is designed as an informational website. If analytics, advertising, contact forms, or similar features are added, they may collect usage data such as pages visited, device information, browser type, approximate location, referral source, and interaction events.</p>
        <h2>Cookies and advertising</h2>
        <p>Future advertising services, including Google AdSense, may use cookies or similar technologies to serve, measure, and personalize ads according to their own policies. Visitors can manage cookies through browser settings and applicable consent controls.</p>
        <h2>How information is used</h2>
        <p>Data may be used to operate the site, improve content, understand calculator usage, prevent abuse, measure advertising performance, and comply with legal obligations.</p>
        <h3>Third-party links</h3>
        <p>United Calculator may link to external resources. We are not responsible for third-party privacy practices, content, or policies.</p>
      </>
    ),
  },
  "/terms-and-conditions": {
    title: "Terms and Conditions",
    description: "Terms and Conditions for using United Calculator calculators, guides, pages, and content.",
    body: (
      <>
        <h2>Use of the website</h2>
        <p>By using United Calculator, you agree to use the website for lawful, personal, educational, and informational purposes. You should not misuse the site, interfere with availability, copy content at scale, or rely on calculator results as a substitute for professional advice.</p>
        <h2>Calculator results</h2>
        <p>Calculator outputs are estimates based on the information entered and simplified formulas. Actual results may differ because of local laws, official rules, contracts, rates, taxes, fees, material conditions, health factors, rounding rules, or other real-world variables.</p>
        <h2>Intellectual property</h2>
        <p>Site design, calculator page structure, text, and related materials are provided for United Calculator visitors. Third-party trademarks, tools, and services remain the property of their respective owners.</p>
        <h3>Limitation of liability</h3>
        <p>United Calculator is provided as-is. To the fullest extent permitted by law, we are not liable for losses or damages arising from use of the site or reliance on its content.</p>
      </>
    ),
  },
};

function LegalPage({ path }: { path: string }) {
  const page = legalContent[path];
  if (!page) return <Page404 />;
  return (
    <AppShell>
      <Seo title={page.title} description={page.description} canonicalPath={path} />
      <main className="page-main">
        <nav className="breadcrumbs" aria-label="Breadcrumbs">
          <Link href="/">Home</Link><span>/</span><span>{page.title}</span>
        </nav>
        <section className="category-hero legal-hero">
          <div>
            <p className="eyebrow">United Calculator</p>
            <h1>{page.title}</h1>
            <p>{page.description}</p>
          </div>
        </section>
        <section className="article-copy legal-copy">{page.body}</section>
      </main>
    </AppShell>
  );
}

// ── Mortgage page ─────────────────────────────────────────────────────────────

function MortgagePage() {
  return (
    <AppShell>
      <Seo
        title="Mortgage Calculator — Monthly Payment, Amortization & Equity Chart"
        description="Free mortgage calculator with amortization table, equity vs interest area chart, multi-currency support, and CSV export. Works for 10–30 year loans in USD, EUR, GBP, CAD, AUD, INR, and JPY."
        canonicalPath="/mortgage-calculator"
      />
      <main className="page-main">
        <nav className="breadcrumbs" aria-label="Breadcrumbs">
          <Link href="/">Home</Link><span>/</span>
          <Link href={withTrailingSlash("/category/finance")}>Finance</Link><span>/</span>
          <span>Mortgage Calculator</span>
        </nav>
        <section className="category-hero">
          <p className="eyebrow">Finance · Property</p>
          <h1>Mortgage Calculator</h1>
          <p>Calculate your monthly mortgage payment, total interest, and full amortization schedule. Supports multiple currencies, loan terms from 10 to 30 years, and CSV export.</p>
        </section>
        <Suspense fallback={<div className="engine-loading">Loading calculator…</div>}>
          <MortgageCalculator />
        </Suspense>
        <section className="article-copy">
          <h2>How mortgage payments are calculated</h2>
          <p>The standard mortgage payment formula uses the loan principal <em>P</em>, monthly interest rate <em>r</em> (annual rate ÷ 12), and total number of payments <em>n</em> (years × 12):</p>
          <p><strong>Monthly payment = P × [r(1+r)ⁿ] ÷ [(1+r)ⁿ − 1]</strong></p>
          <p>Each payment covers the accrued interest first; the remainder reduces the principal. Early in the loan, most of each payment is interest. Over time the split reverses — this is called amortization.</p>
          <h2>Reading the equity chart</h2>
          <p>The <em>cumulative equity</em> line shows how much of the home you own outright (down payment + principal repaid). The <em>annual interest</em> bars show what you paid to the lender each year. Notice how interest peaks early and falls sharply in later years as the balance shrinks.</p>
          <h2>Tips for reducing total interest</h2>
          <ul>
            <li>A larger down payment reduces the principal, which cuts interest significantly over a 30-year term.</li>
            <li>Even one extra monthly payment per year can shave years off a 30-year loan.</li>
            <li>Compare a 15-year vs 30-year term: the monthly payment is higher but total interest is often half.</li>
            <li>Watch for points and origination fees — a lower headline rate is not always cheaper after fees.</li>
          </ul>
          <h2>Frequently asked questions</h2>
          <div className="faq-list">
            {[
              { q: "Does this include property taxes and insurance?", a: "No — this calculator computes principal and interest (P&I) only. Your actual monthly outgoing will be higher when you add property taxes, homeowner's insurance (hazard insurance), and any required PMI (private mortgage insurance if your down payment is below 20%)." },
              { q: "What is PMI and when can I remove it?", a: "PMI is an insurance premium required by most US lenders when the loan-to-value ratio is above 80% (i.e. down payment under 20%). Once your equity reaches 20% of the original appraised value — tracked by this calculator's equity line — you can typically request PMI cancellation." },
              { q: "How accurate is this calculator?", a: "The calculation uses the standard annuity amortization formula used by banks globally. Results match lender amortization schedules when the rate, term, and principal match. It does not account for bi-weekly payments, balloon payments, adjustable rates, or fee-included APR." },
              { q: "Can I use this for non-US mortgages?", a: "Yes — select your currency from the dropdown. The formula is currency-agnostic. Note that mortgage conventions (compounding frequency, prepayment rules, stress-test rates) vary by country; confirm local requirements with your lender." },
            ].map((item, i) => (
              <details key={i} open={i === 0}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </section>
      </main>
    </AppShell>
  );
}

// ── Router ────────────────────────────────────────────────────────────────────

function Router() {
  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/" component={Home} />
        <Route path="/blog" component={BlogHome} />
        <Route path="/blog/category/:cat" component={BlogCategoryPageWrapper} />
        <Route path="/blog/:slug" component={BlogPostPage} />
        <Route path="/calculators/:superSlug" component={SuperCategoryPage} />
        <Route path="/category/:slug" component={CategoryPage} />
        <Route path="/calculator/:slug" component={CalculatorPage} />
        <Route path="/mortgage-calculator" component={MortgagePage} />
        <Route path="/legal">{() => <AppShell><LegalHub /></AppShell>}</Route>
        <Route path="/about">{() => <LegalPage path="/about" />}</Route>
        <Route path="/disclaimer">{() => <LegalPage path="/disclaimer" />}</Route>
        <Route path="/privacy-policy">{() => <LegalPage path="/privacy-policy" />}</Route>
        <Route path="/terms-and-conditions">{() => <LegalPage path="/terms-and-conditions" />}</Route>
        <Route component={Page404} />
      </Switch>
    </>
  );
}

// ── App root ──────────────────────────────────────────────────────────────────

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
          <Router />
        </WouterRouter>
        <Toaster />
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
